import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { FlightDetails } from '../interface/flight.details'
import './SingleFlightBoarding.css'
import { BUCKET_URL } from '../utils/env-utils'
import { getRemarkKey, RemarkToCodeMapping, TripType } from '@industronics/fids-utils'
import StationsRotator from './StationsRotator'
import AnimatedCodeshare from './AnimatedCodeshare'
import RemarksRotator from './RemarksRotator'
import mahbLogo from '../assets/MAHB_LOGO.jpg'
import type { Announcement } from '../interface/device.details'
import { isAnnouncementActive } from './announcements/announcement.utils'
import ScrollingText from './scrolling/ScrollingText'

function getRemarkBadgeColor(remarkKey: RemarkToCodeMapping | undefined): string {
  switch (remarkKey) {
    case RemarkToCodeMapping.DELAYED:
    case RemarkToCodeMapping.CANCELLED:
    case RemarkToCodeMapping.GATE_CLOSED:
    case RemarkToCodeMapping.GATE_CHANGE:
      return 'd4-remark--red'
    case RemarkToCodeMapping.FINAL_CALL:
      return 'd4-remark--amber'
    case RemarkToCodeMapping.BOARDING:
    case RemarkToCodeMapping.GO_TO_GATE:
    case RemarkToCodeMapping.GATE_OPEN:
    case RemarkToCodeMapping.OPEN:
      return 'd4-remark--green'
    case RemarkToCodeMapping.DEPARTED:
    case RemarkToCodeMapping.LANDED:
      return 'd4-remark--cyan'
    default:
      return ''
  }
}

interface SingleFlightBoardingProps {
  gate: string
  flight: FlightDetails
  isNext?: boolean
  enableNextFlight?: boolean
  announcement?: Announcement
}

const SingleFlightBoarding: React.FC<SingleFlightBoardingProps> = ({
  gate,
  flight,
  isNext = false,
  enableNextFlight,
  announcement,
}) => {
  const effectiveIsNext = enableNextFlight === false ? false : isNext
  const [currentTime, setCurrentTime] = useState(moment())
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(moment()), 1000)
    return () => clearInterval(interval)
  }, [])

  const flightNumberPrefix = useMemo(
    () => flight?.flightNumber?.split(' ')[0] ?? 'DEFAULT',
    [flight?.flightNumber]
  )
  const globalStartTime = useRef(performance.now()).current
  const codeshareItemHeight = Math.round(window.innerHeight * 0.18)

  const remarksList = useMemo<string[]>(() => {
    if (flight.isGateChange && flight.oldLoungeCode === gate) {
      return [getRemarkKey(RemarkToCodeMapping.GATE_CHANGE)]
    }
    return Array.isArray(flight?.remarkCode?.translations)
      ? flight.remarkCode.translations.length
        ? flight.remarkCode.translations.map((item: { value: any }) => item.value)
        : [flight.remarkCode.description]
      : []
  }, [flight.isGateChange, flight.oldLoungeCode, flight.remarkCode?.translations, gate]);
  console.log("remarksList", remarksList);

  const stations = useMemo<string[]>(() => {
    if (flight.tripType === TripType.Arrival) {
      return flight.stations.slice(0, -1).flatMap(
        (station: { translations: any[] }) => station.translations?.map(t => t.value) ?? []
      )
    } else if (flight.tripType === TripType.Departure) {
      return flight.stations.slice(1).flatMap(
        (station: { translations: any[] }) => station.translations?.map(t => t.value) ?? []
      )
    }
    return []
  }, [flight.stations, flight.tripType])

  const boardingTime = useMemo(() => {
    if (!flight?.loungeBoardingTime && !flight?.schLoungeBoardingTime) return ''
    return moment(flight.loungeBoardingTime || flight.schLoungeBoardingTime).format('HH:mm')
  }, [flight?.loungeBoardingTime, flight?.schLoungeBoardingTime])

  const isGateChangeForThisGate = flight.isGateChange && flight.oldLoungeCode === gate

  const remarkKey = useMemo(() => {
    if (isGateChangeForThisGate) return RemarkToCodeMapping.GATE_CHANGE
    return flight?.remarkCode?.key
  }, [isGateChangeForThisGate, flight?.remarkCode?.key])

  const badgeColor = isGateChangeForThisGate ? 'd4-remark--red' : getRemarkBadgeColor(remarkKey)

  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = `${BUCKET_URL}/1366x0200/${flightNumberPrefix}.jpg`
  }

  const renderDayLabel = (time: moment.MomentInput) => {
    const m = moment(time)
    if (!m.utcOffset(8).isSame(moment(), 'day')) {
      return (
        <span className="bp-day-label">
          {m.format('DD MMM')}
        </span>
      )
    }
    return null
  }

  const hours = currentTime.utcOffset(8).format('HH')
  const minutes = currentTime.utcOffset(8).format('mm')
  const dateStr = currentTime.utcOffset(8).format('MMM DD, ddd')

  return (
    <div className="bp-page">
      {/* Header */}
      <div className="fids-header">
        <span className="fids-header-label">
          <span className="fids-header-label-dim">GATE</span>
          {' '}
          <span className="fids-header-label-bold">{gate}</span>
        </span>
        <div className="fids-header-datetime">
          <span className="fids-header-date">{dateStr}</span>
        </div>
      </div>

      <div className="bp-boarding-pass">

        {/* Airline header banner */}
        <div className="bp-airline-banner">
          <img
            className="bp-airline-banner-img"
            src={`${BUCKET_URL}/1366x0200/${flightNumberPrefix}.bmp`}
            onError={handleLogoError}
            alt="Airline logo"
          />
        </div>

        <div className="bp-body">

          {/* Row 1: NEXT FLIGHT badge */}
          {effectiveIsNext && (
            <div className="bp-next-badge">NEXT FLIGHT</div>
          )}

          {/* Row 2: Flight number (left) + Codeshare (right) */}
          <div className="bp-flight-row">
            <div className="bp-flight-number">{flight?.flightNumber}</div>
            {flight.codeShareFlight?.length > 0 && (
              <div className="bp-codeshare-wrap">
                <AnimatedCodeshare
                  labelClassName="bp-codeshare-label"
                  codeShareFlights={flight.codeShareFlight}
                  itemHeight={codeshareItemHeight}
                />
              </div>
            )}
          </div>

          {/* Row 3: Destination */}
          <div className="bp-destination">
            {flight?.stations?.length > 0 && (
              <StationsRotator
                lastIndex={-1}
                globalStartTime={globalStartTime}
                items={stations}
                mainFlightNumber={flight.flightNumberSuffix}
              />
            )}
          </div>

          {/* Info grid: 4-col (label | value | label | value) x 2 rows */}
          <div className="bp-info-grid">
            {/* Row 1 */}
            <span className="bp-time-label">SCHEDULED:</span>
            <span className="bp-time-value bp-text-green">
              {flight?.scheduledTravelTime
                ? moment(flight.scheduledTravelTime).format('HH:mm')
                : '—'}
              {flight?.scheduledTravelTime && renderDayLabel(flight.scheduledTravelTime)}
            </span>
            <span className="bp-time-label">BOARDING:</span>
            <span className="bp-time-value bp-text-yellow">
              {boardingTime || '—'}
            </span>

            {/* Row 2 */}
            <span className="bp-time-label">ESTIMATED:</span>
            <span className="bp-time-value bp-text-red">
              {flight?.ETA
                ? moment(flight.ETA).format('HH:mm')
                : '—'}
              {flight?.ETA && renderDayLabel(flight.ETA)}
            </span>
            <span className="bp-time-label">REMARK:</span>
            <span className={`bp-remark-value ${isGateChangeForThisGate ? 'bp-text-red blink-hard' : ''}`}>
              {!effectiveIsNext && remarksList.length > 0
                ? <RemarksRotator
                    cssClass='bp-remark-text'
                    globalStartTime={globalStartTime}
                    mainFlightNumber={flight.flightNumber}
                    items={remarksList}
                  />
                : !effectiveIsNext
                ? 'NEXT FLIGHT'
                : '—'}
            </span>
          </div>

        </div>
      </div>
      {/* Footer */}
      <div className="fids-footer">
        {announcement?.messageContent?.lines?.length ? (
          <div className="fids-footer-marquee">
            <ScrollingText
              sentences={announcement.messageContent.lines}
              speed={announcement.messageContent.speed || 50}
              separator=" &bull; "
              backgroundColor={announcement.messageContent.backgroundColor ?? 'transparent'}
              textColor={announcement.messageContent.textColor ?? 'white'}
              fontFamily={announcement.messageContent.fontFamily ?? 'Arial, sans-serif'}
              fontSize={announcement.messageContent.fontSize ?? 16}
              noScroll={announcement.messageContent.noScroll ?? false}
            />
          </div>
        ) : (
          <div className="fids-footer-spacer" />
        )}
        <div className="fids-footer-divider" />
        <div className="fids-footer-logo">
          <img src={mahbLogo} alt="MAHB Logo" className="fids-footer-logo-img" />
        </div>
      </div>
    </div>
  )
}

export default SingleFlightBoarding